/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Location
} from '@angular/common';
import {
	Component,
	OnInit
} from '@angular/core';
import {
	EntityTypeApiService
} from '@api/services/entities/entity-type.api.service';
import {
	EntityInstanceComponent
} from '@entity/components/entity-instance/entity-instance.component';
import {
	EntityService
} from '@entity/services/entity.service';
import {
	PaymentBatchConstants
} from '@shared/constants/payment-batch-constants';
import {
	DrawerListDirective
} from '@shared/directives/drawer-list-directive';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IOwnershipGuardComponent
} from '@shared/interfaces/application-objects/ownership-guard-component';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	IEntityType
} from '@shared/interfaces/entities/entity-type.interface';
import {
	SessionService
} from '@shared/services/session.service';

@Component({
	selector: 'app-payment-batch',
	templateUrl: './payment-batch.component.html',
	styleUrls: [
		'./payment-batch.component.scss'
	]
})

/**
 * A component representing context level payment batch handling.
 *
 * @export
 * @class PaymentBatchComponent
 * @extends {DrawerListDirective<IEntityInstance>}
 * @implements {IDynamicComponent<Component, any>}
 * @implements {IOwnershipGuardComponent}
 */
export class PaymentBatchComponent
	extends DrawerListDirective<IEntityInstance>
	implements OnInit, IDynamicComponent<Component, any>,
		IOwnershipGuardComponent
{
	/**
	 * Initializes a new instance of the payment batch component.
	 *
	 * @param {EntityService} entityService
	 * The entity service.
	 * @param {EntityTypeApiService} entityTypeApiService
	 * The entity type api service.
	 * @param {SessionService} sessionService
	 * The session service.
	 * @param {Location} location
	 * The location object.
	 * @memberof PaymentBatchComponent
	 */
	public constructor(
		public entityService: EntityService,
		public entityTypeApiService: EntityTypeApiService,
		public sessionService: SessionService,
		public location: Location)
	{
		super();
	}

	/**
	 * Handles the on initialization event.
	 * This method will set configurable properties used in the drawer list
	 * directive and this component's view.
	 *
	 * @async
	 * @memberof PaymentBatchComponent
	 */
	public async ngOnInit(): Promise<void>
	{
		this.entityDisplayName = 'Payment Batch';
		this.wildcardChildFilter = 'PaymentBatch';

		const entityInstanceComponent: EntityInstanceComponent =
			<EntityInstanceComponent>this.context.source;

		if (!await this.isPageOwnershipAllowed())
		{
			this.accessDeniedUrl = this.location.path();
			this.sessionIdentifier = this.sessionService.sessionId;
			this.resources =
				[
					PaymentBatchConstants.paymentBatchIdentifiers
						.entityTypeIdentifier
				];
			this.clientMessage =
				'Access is required to at least one payment batch '
					+ 'entity type and version.';
			this.isOwnershipAllowed = false;

			return;
		}

		this.initialCreateData =
			{
				type: entityInstanceComponent.entityType.name,
				references: [
					{
						type: PaymentBatchConstants.paymentBatchIdentifiers
							.parentNavigationEntityIdentifer,
						identifier:
							entityInstanceComponent.entityInstance.id
					},
					{
						type: PaymentBatchConstants.paymentBatchIdentifiers
							.parentNavigationEntityType,
						identifier:
							entityInstanceComponent.entityInstance.entityType
					}
				]
			};

		this.context.data =
			{
				...this.context.data,
				drawerComponent: this
			};

		await this.setSecurityDefinitions(
			entityInstanceComponent.id,
			entityInstanceComponent.entityType.group,
			entityInstanceComponent.entityDefinition,
			entityInstanceComponent.entityInstanceApiService,
			entityInstanceComponent.entityTypeApiService);
	}

	/**
	* Implements the ownership guard interface.
	* This will calculate drawer ownership permissions.
	*
	* @async
	* @returns {Promise<boolean>}
	* A value signifying whether or not access is allowed to this drawer.
	* @memberof PaymentBatchComponent
	*/
	public async isPageOwnershipAllowed(): Promise<boolean>
	{
		await this.entityService.setStoredVariables();

		const paymentBatchItemTypes: IEntityType[] =
			this.entityService
				.entityTypes
				.filter(
					(entityType: IEntityType) =>
						entityType.group.indexOf(
							PaymentBatchConstants.paymentBatchIdentifiers
								.entityTypePrefix) !== -1);

		if (paymentBatchItemTypes.length === 0)
		{
			return false;
		}

		const initialPromiseArray: Promise<any>[] = [];
		paymentBatchItemTypes.forEach(
			(entityType: IEntityType) =>
			{
				initialPromiseArray.push(
					this.entityService.verifyEntityTypeAccess(
						entityType));
			});

		const allowedEntities: boolean[] =
		await Promise.all(initialPromiseArray);

		return allowedEntities.some(
			(allowed: boolean) =>
				allowed === true);
	}
}